import { PhraseHighlight } from '@/components/displays/PhraseHighlight';
import { Result, SlideComponentHkcsQ2 } from '@/types/slide';
import { Box, Skeleton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { ImageShowcase } from '../../ImageShowcase';
import { PropsWithOnLoad } from '../../type';
import { ResultContentContainer } from './ResultContentContainer';

type HkcsQ2Answer = {
    hkcsQ2: {
        textContent: string;
        answer: string;
    };
};

const isHkcsQ2Answer = (answer: unknown): answer is HkcsQ2Answer => {
    return (
        typeof answer === 'object' &&
        answer !== null &&
        'hkcsQ2' in answer &&
        typeof (answer as HkcsQ2Answer).hkcsQ2 === 'object' &&
        (answer as HkcsQ2Answer).hkcsQ2 !== null &&
        'textContent' in (answer as HkcsQ2Answer).hkcsQ2 &&
        typeof (answer as HkcsQ2Answer).hkcsQ2.textContent === 'string' &&
        'answer' in (answer as HkcsQ2Answer).hkcsQ2 &&
        typeof (answer as HkcsQ2Answer).hkcsQ2.answer === 'string'
    );
};

export const ResultContentHkcsQ2: React.FC<
    PropsWithOnLoad & {
        question: SlideComponentHkcsQ2 & { answers: Result[] };
    }
> = ({ question, onLoad }) => {
    const answer = question.answers[0];
    const data = answer?.data ? JSON.parse(answer.data) : undefined;

    const [loadCount, setLoadCount] = useState<number>((answer?.fetchResultFiles ? 1 : 0) + (question.content.imageURL || question.content.image ? 1 : 0));

    const [loading, setLoading] = useState<boolean | undefined>(answer?.fetchResultFiles ? true : false);
    const [drawing, setDrawing] = useState<string | undefined>(undefined);

    const { textContent, answer: studentAnswer } = isHkcsQ2Answer(data) ? data.hkcsQ2 : { textContent: '', answer: '' };
    const highlight = studentAnswer ? textContent.search(studentAnswer) : null;

    const handleLoadImage = () => {
        setLoadCount((prev) => prev - 1);
    };

    useEffect(() => {
        if (answer?.fetchResultFiles) {
            setLoading(true);
            answer
                .fetchResultFiles()
                .then((files) => {
                    if (files && files.length > 0) {
                        setDrawing(files[0]?.url);
                    } else {
                        setDrawing(undefined);
                        setLoadCount((prev) => prev - 1);
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setDrawing(undefined);
        }
    }, [answer]);

    useEffect(() => {
        if (loadCount <= 0) {
            onLoad?.();
        }
    }, [loadCount, onLoad]);

    return (
        <ResultContentContainer>
            <Box data-mui-color-scheme='light' sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        width: 594,
                        height: 240,
                        gap: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <PhraseHighlight sx={{ color: '#555', my: 2 }} phrase={question.content.phrase} highlight={question.content.phrase} underline={false} />
                        <ImageShowcase
                            width={150}
                            src={
                                question.content.imageURL && question.content.imageURL !== ''
                                    ? question.content.imageURL
                                    : `/asset/characters/${question.content.image ? question.content.image + '/image.png' : 'default.png'}`
                            }
                            onLoad={handleLoadImage}
                        />
                    </Box>
                    <Box sx={{ height: 240, width: '100%' }}>
                        {drawing ? (
                            <Box
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    borderRadius: 2,
                                    borderColor: 'divider',
                                    borderWidth: 2,
                                    display: 'flex',
                                    flex: 1,
                                    backgroundColor: 'white',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Box
                                    component={'img'}
                                    src={drawing}
                                    sx={{
                                        height: '100%',
                                        width: '100%',
                                        borderRadius: 2,
                                        borderColor: 'divider',
                                        borderWidth: 2,
                                    }}
                                    onLoad={handleLoadImage}
                                />
                            </Box>
                        ) : loading ? (
                            <Skeleton variant='rounded' sx={{ height: '100%', width: '100%', borderRadius: 2 }} />
                        ) : (
                            <Box
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    backgroundColor: 'white',
                                    borderRadius: 2,
                                    borderColor: 'divider',
                                    borderWidth: 2,
                                    flex: 1,
                                }}
                            />
                        )}
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateRows: 'repeat(2, 1fr)',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '1px',
                        backgroundColor: 'divider',
                        height: 'fit-content',
                        width: 'fit-content',
                        border: 1,
                        borderColor: 'divider',
                    }}
                >
                    {[...Array(14)].map((_, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                height: '84px',
                                width: '84px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: (theme) =>
                                    highlight !== null && highlight >= 0 && idx >= highlight && idx < highlight + studentAnswer.length ? (theme.vars || theme).palette.primary.main : 'white',
                            }}
                        >
                            <Typography variant='h2' sx={{ color: 'black' }}>
                                {textContent[idx] || ''}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </ResultContentContainer>
    );
};
